import {
  Timestamp,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type {
  Company,
  DutyCheckIn,
  DutyExpense,
  DutySession,
  DutyStatus,
  DutyTaskLinkEntry,
  FieldCheckInMessageData,
  LiveLocation,
  LocationIntegrityAttempt,
  Member,
  Task,
  TaskMessage,
  TrackPoint,
} from './models';
import { roleFromFirestore } from './models';
import { clampDutyAutoEndMinutesIst } from './dutyAutoEnd';
import {
  clampExpenseHomeRadiusKm,
  clampExpensePostDutyWindowHours,
  clampExpenseTravelRatePerKm,
} from './expenseConfig';
import { clampStationaryMarkMinutes } from './stationaryDwell';
import { parseTaskAppreciations } from './taskAppreciation';
import { normalizeQuickFilterIds } from './taskListFilters';

function toDateOrUndefined(v: unknown): Date | undefined {
  if (v == null) return undefined;
  return toDate(v);
}

function toNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toNumberOrUndefined(v: unknown): number | undefined {
  if (v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function dateKeyFromInstant(at: Date): string {
  // IST = UTC+5:30
  const istMs = at.getTime() + (5 * 60 + 30) * 60 * 1000;
  const d = new Date(istMs);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function toDate(v: unknown): Date {
  if (!v) return new Date();
  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as Timestamp).toDate();
  }
  const p = Date.parse(String(v));
  return Number.isNaN(p) ? new Date() : new Date(p);
}

function stringList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((e) => String(e)).filter(Boolean);
}

function parseTags(data: DocumentData): string[] {
  const tagsRaw = data.tags;
  if (Array.isArray(tagsRaw)) {
    return tagsRaw.map((e) => String(e).trim()).filter(Boolean);
  }
  const tagStr = data.tag as string | undefined;
  if (tagStr && tagStr.trim()) return [tagStr.trim()];
  return [];
}

export function taskFromSnapshot(
  snap: QueryDocumentSnapshot | DocumentSnapshot,
  companyId: string,
): Task {
  const data = snap.data() as DocumentData;
  return {
    id: snap.id,
    companyId,
    title: (data.title as string) ?? '',
    description: data.description as string | undefined,
    tags: parseTags(data),
    createdBy: (data.createdBy as string) ?? '',
    createdAt: toDate(data.createdAt),
    lastMessageAt:
      data.lastMessageAt != null ? toDate(data.lastMessageAt) : undefined,
    eta: data.eta != null ? toDate(data.eta) : undefined,
    repeat: data.repeat as string | undefined,
    repeatTime: data.repeatTime as string | undefined,
    repeatEtaTime: data.repeatEtaTime as string | undefined,
    taskNumber:
      typeof data.taskNumber === 'number' ? data.taskNumber : undefined,
    taskDisplayCode: data.taskDisplayCode as string | undefined,
    status: data.status === 'closed' ? 'closed' : 'open',
    closedBy: data.closedBy as string | undefined,
    closedAt: data.closedAt != null ? toDate(data.closedAt) : undefined,
    assignees: stringList(data.assignees),
    primaryAssigneeId: (() => {
      const raw = data.primaryAssigneeId;
      if (typeof raw === 'string') {
        const t = raw.trim();
        return t === '' ? undefined : t;
      }
      return undefined;
    })(),
    supervisors: stringList(data.supervisors),
    hasClosureRequest: data.closureRequest != null,
    closureRequestedAt: (() => {
      const cr = data.closureRequest;
      if (cr == null || typeof cr !== 'object') return undefined;
      return toDateOrUndefined((cr as DocumentData).requestedAt);
    })(),
    lastClosureRequestedAt: (() => {
      const lr = data.lastClosureRequest;
      if (lr == null || typeof lr !== 'object') return undefined;
      return toDateOrUndefined((lr as DocumentData).requestedAt);
    })(),
    lastClosureRequestStatus: (() => {
      const lr = data.lastClosureRequest;
      if (lr == null || typeof lr !== 'object') return undefined;
      const s = (lr as DocumentData).status;
      return typeof s === 'string' && s.trim() ? s.trim() : undefined;
    })(),
    closedWithoutClosureRequest: data.closedWithoutClosureRequest === true,
    closedAsNotDone: data.closedAsNotDone === true,
    isBlocked: data.taskBlock != null,
    isEtaIncreaseRequested: data.etaIncreaseRequest != null,
    appreciations: parseTaskAppreciations(data as Record<string, unknown>),
  };
}

export function companyFromSnapshot(
  doc: QueryDocumentSnapshot | DocumentSnapshot,
  backgroundOverride?: string,
): Company {
  const data = doc.data() as DocumentData;
  return {
    id: doc.id,
    name: (data.name as string) ?? '',
    businessName: data.businessName as string | undefined,
    colorHex:
      typeof data.colorHex === 'number'
        ? data.colorHex
        : Number(data.colorHex) || 0xff3c3c43,
    joinCode: data.joinCode as string | undefined,
    logoUrl: data.logoUrl as string | undefined,
    description: data.description as string | undefined,
    backgroundAsset: backgroundOverride ?? (data.backgroundAsset as string | undefined),
    autoSelectSupervisorsOnNewTasks:
      data.autoSelectSupervisorsOnNewTasks !== false,
    dutyAutoEndMinutesIst: clampDutyAutoEndMinutesIst(
      data.dutyAutoEndMinutesIst as number | undefined,
    ),
    stationaryMarkMinutes: clampStationaryMarkMinutes(
      data.stationaryMarkMinutes as number | undefined,
    ),
    expensesEnabled: data.expensesEnabled === true,
    expenseHomePinCode: data.expenseHomePinCode as string | undefined,
    expenseHomeLat: toNumberOrUndefined(data.expenseHomeLat),
    expenseHomeLng: toNumberOrUndefined(data.expenseHomeLng),
    expenseHomeRadiusKm: clampExpenseHomeRadiusKm(
      data.expenseHomeRadiusKm as number | undefined,
    ),
    expenseTravelRatePerKm: clampExpenseTravelRatePerKm(
      data.expenseTravelRatePerKm as number | undefined,
    ),
    expensePostDutyWindowHours: clampExpensePostDutyWindowHours(
      data.expensePostDutyWindowHours as number | undefined,
    ),
    quickFilterIds: normalizeQuickFilterIds(data.quickFilterIds),
    billingPlan: data.billingPlan as string | undefined,
    premiumComplimentaryEndsAt: toDateOrUndefined(data.premiumComplimentaryEndsAt),
    premiumSubscriptionPaidThrough: toDateOrUndefined(
      data.premiumSubscriptionPaidThrough,
    ),
    createdAt: toDate(data.createdAt),
    createdBy: (data.createdBy as string) ?? '',
  };
}

export function memberFromSnapshot(
  uid: string,
  data: DocumentData,
): Member {
  return {
    uid,
    role: roleFromFirestore(data.role as string),
    displayName: data.displayName as string | undefined,
    phoneNumber: data.phoneNumber as string | undefined,
    joinedAt: toDate(data.joinedAt),
    backgroundAsset: data.backgroundAsset as string | undefined,
    fieldDutyTrialSessionsUsed:
      typeof data.fieldDutyTrialSessionsUsed === 'number'
        ? Math.max(0, Math.floor(data.fieldDutyTrialSessionsUsed))
        : undefined,
  };
}

function linkHistoryFrom(raw: unknown): DutyTaskLinkEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
    .map((e) => ({
      taskId: (e.taskId as string) ?? '',
      linkedAt: toDate(e.linkedAt),
      unlinkedAt: toDateOrUndefined(e.unlinkedAt),
      taskTitle: e.taskTitle as string | undefined,
      taskDisplayCode: e.taskDisplayCode as string | undefined,
    }))
    .filter((e) => e.taskId.length > 0);
}

export function dutySessionFromSnapshot(
  snap: QueryDocumentSnapshot | DocumentSnapshot,
): DutySession {
  const data = snap.data() as DocumentData;
  const startAt = toDate(data.startAt);
  const status: DutyStatus = data.status === 'active' ? 'active' : 'ended';
  return {
    id: snap.id,
    userId: (data.userId as string) ?? '',
    userName: (data.userName as string) ?? '',
    status,
    startAt,
    endAt: toDateOrUndefined(data.endAt),
    dateKey: (data.dateKey as string) ?? '',
    durationMinutes: Math.round(toNumber(data.durationMinutes)),
    distanceMeters: toNumber(data.distanceMeters),
    startLat: toNumberOrUndefined(data.startLat),
    startLng: toNumberOrUndefined(data.startLng),
    endLat: toNumberOrUndefined(data.endLat),
    endLng: toNumberOrUndefined(data.endLng),
    dutyChainId: data.dutyChainId as string | undefined,
    linkedTaskId: data.linkedTaskId as string | undefined,
    linkedAt: toDateOrUndefined(data.linkedAt),
    linkedTaskTitle: data.linkedTaskTitle as string | undefined,
    linkedTaskDisplayCode: data.linkedTaskDisplayCode as string | undefined,
    linkHistory: linkHistoryFrom(data.linkHistory),
    matchStatus: data.matchStatus as string | undefined,
    matchedDistanceMeters:
      data.matchedDistanceMeters == null
        ? undefined
        : toNumber(data.matchedDistanceMeters),
    matchedPolyline: data.matchedPolyline as string | undefined,
    matchSource: data.matchSource as string | undefined,
    osrmPolyline: data.osrmPolyline as string | undefined,
    osrmDistanceMeters:
      data.osrmDistanceMeters == null
        ? undefined
        : toNumber(data.osrmDistanceMeters),
    matchedPointCount:
      data.matchedPointCount == null
        ? undefined
        : Math.round(toNumber(data.matchedPointCount)),
    matchAttempts:
      data.matchAttempts == null ? undefined : Math.round(toNumber(data.matchAttempts)),
    matchError: data.matchError as string | undefined,
    securityFlags: Array.isArray(data.securityFlags)
      ? data.securityFlags.map((v) => String(v).trim()).filter(Boolean)
      : undefined,
    securityFlaggedAt: toDateOrUndefined(data.securityFlaggedAt),
    securityFlagMessage: data.securityFlagMessage as string | undefined,
    gpsOffActive: typeof data.gpsOffActive === 'boolean' ? data.gpsOffActive : undefined,
    trackingGapActive:
      typeof data.trackingGapActive === 'boolean' ? data.trackingGapActive : undefined,
    batteryStartPercent:
      data.batteryStartPercent == null
        ? undefined
        : Math.round(toNumber(data.batteryStartPercent)),
    batteryEndPercent:
      data.batteryEndPercent == null
        ? undefined
        : Math.round(toNumber(data.batteryEndPercent)),
    batteryLastPercent:
      data.batteryLastPercent == null
        ? undefined
        : Math.round(toNumber(data.batteryLastPercent)),
    batteryLastAt: toDateOrUndefined(data.batteryLastAt),
    expenseEligible: data.expenseEligible === true,
  };
}

export function dutyExpenseFromSnapshot(
  snap: QueryDocumentSnapshot | DocumentSnapshot,
): DutyExpense {
  const data = snap.data() as DocumentData;
  const type = data.type === 'travel' ? 'travel' : 'manual';
  const statusRaw = String(data.status ?? 'pending');
  const status =
    statusRaw === 'approved' || statusRaw === 'rejected' ? statusRaw : 'pending';
  return {
    id: snap.id,
    userId: String(data.userId ?? ''),
    userName: String(data.userName ?? ''),
    sessionId: String(data.sessionId ?? ''),
    dateKey: String(data.dateKey ?? ''),
    type,
    amountInr: toNumber(data.amountInr),
    status,
    description: String(data.description ?? ''),
    receiptUrls: stringList(data.receiptUrls),
    distanceKm: toNumberOrUndefined(data.distanceKm),
    ratePerKm: toNumberOrUndefined(data.ratePerKm),
    rejectionReason: data.rejectionReason as string | undefined,
    resubmitNote: data.resubmitNote as string | undefined,
    reviewedBy: data.reviewedBy as string | undefined,
    reviewedByName: data.reviewedByName as string | undefined,
    reviewedAt: toDateOrUndefined(data.reviewedAt),
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

export function locationIntegrityAttemptFromSnapshot(
  snap: QueryDocumentSnapshot | DocumentSnapshot,
): LocationIntegrityAttempt {
  const data = snap.data() as DocumentData;
  const at = toDate(data.at);
  return {
    id: snap.id,
    userId: String(data.userId ?? ''),
    userName: String(data.userName ?? ''),
    at,
    dateKey: String(data.dateKey ?? '').trim() || dateKeyFromInstant(at),
    action: String(data.action ?? ''),
    reason: String(data.reason ?? ''),
    sessionId: data.sessionId == null ? undefined : String(data.sessionId),
    lat: toNumberOrUndefined(data.lat),
    lng: toNumberOrUndefined(data.lng),
    accuracy: toNumberOrUndefined(data.accuracy),
    isMocked: typeof data.isMocked === 'boolean' ? data.isMocked : undefined,
    message: data.message == null ? undefined : String(data.message),
  };
}

export function trackPointFromSnapshot(
  snap: QueryDocumentSnapshot | DocumentSnapshot,
): TrackPoint {
  const data = snap.data() as DocumentData;
  return {
    lat: toNumber(data.lat),
    lng: toNumber(data.lng),
    at: toDate(data.at),
    accuracy: toNumberOrUndefined(data.accuracy),
    speed: toNumberOrUndefined(data.speed),
    seq: Math.round(toNumber(data.seq)),
  };
}

export function liveLocationFromSnapshot(
  snap: QueryDocumentSnapshot | DocumentSnapshot,
): LiveLocation {
  const data = snap.data() as DocumentData;
  return {
    userId: (data.userId as string) ?? snap.id,
    userName: (data.userName as string) ?? '',
    lat: toNumber(data.lat),
    lng: toNumber(data.lng),
    at: toDate(data.at),
    onDuty: data.onDuty === true,
    sessionId: data.sessionId as string | undefined,
    speed: toNumberOrUndefined(data.speed),
    accuracy: toNumberOrUndefined(data.accuracy),
  };
}

export function dutyCheckInFromSnapshot(
  snap: QueryDocumentSnapshot | DocumentSnapshot,
): DutyCheckIn {
  const data = snap.data() as DocumentData;
  const rawPhotos = data.photoUrls;
  const photoUrls = Array.isArray(rawPhotos)
    ? rawPhotos.filter((u): u is string => typeof u === 'string')
    : [];
  return {
    id: snap.id,
    userId: (data.userId as string) ?? '',
    userName: (data.userName as string) ?? '',
    sessionId: (data.sessionId as string) ?? '',
    description: (data.description as string) ?? '',
    photoUrls,
    lat: toNumber(data.lat),
    lng: toNumber(data.lng),
    accuracy: toNumberOrUndefined(data.accuracy),
    createdAt: toDate(data.createdAt),
    dateKey: (data.dateKey as string) ?? '',
    taskId: data.taskId as string | undefined,
  };
}

function fieldCheckInFromMap(raw: unknown): FieldCheckInMessageData | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const map = raw as Record<string, unknown>;
  const checkInId = (map.checkInId as string) ?? '';
  const sessionId = (map.sessionId as string) ?? '';
  if (!checkInId || !sessionId) return undefined;
  const photoUrls = Array.isArray(map.photoUrls)
    ? map.photoUrls.filter((u): u is string => typeof u === 'string')
    : [];
  return {
    checkInId,
    sessionId,
    photoUrls,
    lat: toNumber(map.lat),
    lng: toNumber(map.lng),
    userName: map.userName as string | undefined,
  };
}

export function messageFromSnapshot(
  doc: QueryDocumentSnapshot | DocumentSnapshot,
): TaskMessage {
  const data = doc.data() as DocumentData;
  return {
    id: doc.id,
    senderId: (data.senderId as string) ?? '',
    text: (data.text as string) ?? '',
    createdAt: toDate(data.createdAt),
    type: (data.type as string) ?? 'user',
    attachmentName: data.attachmentName as string | undefined,
    textFormat:
      data.textFormat === 'markdown' ? 'markdown' : undefined,
    fieldCheckIn: fieldCheckInFromMap(data.fieldCheckIn),
    editedAt: toDateOrUndefined(data.editedAt),
    deletedAt: toDateOrUndefined(data.deletedAt),
  };
}
